import { useState, useEffect } from 'react';

interface BiometricMeterProps {
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  unit: string;
  imperialUnit?: string;
  toImperial?: (value: number) => number;
  label: string;
  color?: string;
}

export default function BiometricMeter({
  value,
  onChange,
  min,
  max,
  unit,
  imperialUnit,
  toImperial,
  label,
  color = 'blue'
}: BiometricMeterProps) {
  const [showImperial, setShowImperial] = useState(false);

  const percentage = ((value - min) / (max - min)) * 100;

  const getGradientColor = () => {
    if (color === 'blue') return 'from-blue-500 to-blue-600';
    if (color === 'green') return 'from-green-500 to-green-600';
    if (color === 'purple') return 'from-purple-500 to-purple-600';
    return 'from-blue-500 to-blue-600';
  };

  const getTickColor = () => {
    if (color === 'blue') return 'bg-blue-500';
    if (color === 'green') return 'bg-green-500';
    if (color === 'purple') return 'bg-purple-500';
    return 'bg-blue-500';
  };

  const getTicks = () => {
    const step = (max - min) / 10;
    return Array.from({ length: 11 }, (_, i) => min + step * i);
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(parseFloat(e.target.value));
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className={`inline-flex items-baseline gap-2 bg-gradient-to-r ${getGradientColor()} bg-clip-text text-transparent`}>
          <span className="text-6xl font-bold">
            {showImperial && toImperial ? toImperial(value).toFixed(1) : value.toFixed(1)}
          </span>
          <span className="text-3xl font-semibold">
            {showImperial && imperialUnit ? imperialUnit : unit}
          </span>
        </div>

        {imperialUnit && toImperial && (
          <button
            onClick={() => setShowImperial(!showImperial)}
            className="mt-3 text-sm text-gray-600 hover:text-gray-900 underline"
          >
            Show in {showImperial ? unit : imperialUnit}
          </button>
        )}
      </div>

      <div className="relative px-4">
        <div className="relative h-3 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`absolute left-0 top-0 h-full bg-gradient-to-r ${getGradientColor()} transition-all duration-200 rounded-full`}
            style={{ width: `${percentage}%` }}
          />
        </div>

        <input
          type="range"
          min={min}
          max={max}
          step="0.1"
          value={value}
          onChange={handleSliderChange}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />

        <div
          className={`absolute top-1/2 -translate-y-1/2 w-6 h-6 ${getTickColor()} rounded-full border-4 border-white shadow-lg pointer-events-none transition-all duration-200`}
          style={{ left: `calc(${percentage}% - 12px)` }}
        />
      </div>

      <div className="relative h-12 px-4">
        <div className="flex justify-between items-end h-full">
          {getTicks().map((tick, i) => (
            <div key={i} className="flex flex-col items-center">
              <div
                className={`w-0.5 transition-all ${
                  value >= tick - 5 && value <= tick + 5
                    ? `h-6 ${getTickColor()}`
                    : 'h-3 bg-gray-300'
                }`}
              />
              {i % 2 === 0 && (
                <span className="text-xs text-gray-500 mt-1">
                  {Math.round(tick)}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
